// @flow
import * as React from 'react'
import shortid from 'shortid'
import { useFormContext, Controller } from 'react-hook-form'

import { Flex, List, Button } from 'components/common'
import { ControllerError } from 'components/common/form'
import { TimesListItemEdit } from './times-list-item-edit'
import { useI18n } from '~/core/i18n'
import { useGlobalDispatch } from '~/core/context'
import { useTimesListStyles } from './styles'

import type { TimeItem } from './time-item'

const getTotalMinutesFromTimeString = (time: string) => {
  const [hours, minutes] = time.split(':')
  return Number(hours) * 60 + Number(minutes)
}

type TimesListProps = {
  label?: string,
  isDisabled?: boolean,
  value: TimeItem[],
  onChange: (times: TimeItem[]) => void
}

const TimesList = ({ value: times = [], label, isDisabled, onChange }: TimesListProps) => {
  const [msg] = useI18n()
  const classes = useTimesListStyles()
  const globalDispatch = useGlobalDispatch()

  const handleModalClose = () => globalDispatch({ type: 'modal/close' })
  const handleValueChange = (action: 'create' | 'update' | 'delete', newTime: TimeItem) => {
    let newValue = times

    switch (action) {
      case 'create':
        if (!times.some(time => time.time === newTime.time)) {
          newValue = [...times, newTime]
        }
        break
      case 'update':
        newValue = times.map(time => (time.id === newTime.id ? newTime : time))
        break
      case 'delete':
        newValue = times.filter(time => time.id !== newTime.id)
        break
    }

    return onChange(
      newValue.sort((a, b) => {
        return getTotalMinutesFromTimeString(a.time) - getTotalMinutesFromTimeString(b.time)
      })
    )
  }

  const handleTimeAdd = () => {
    const newTime = { id: shortid(), time: '00:00' }
    globalDispatch({
      type: 'modal/open',
      payload: {
        title: msg('common.controllers.timesList.addTime'),
        content: (
          <TimesListItemEdit
            time={newTime}
            onSave={newTime => {
              handleValueChange('create', newTime)
              handleModalClose()
            }}
          />
        )
      }
    })
  }

  const handleTimeEdit = (id: string) => {
    const time = times.find(time => time.id === id)

    if (time) {
      globalDispatch({
        type: 'modal/open',
        payload: {
          title: msg('common.controllers.timesList.editTime'),
          content: (
            <TimesListItemEdit
              time={time}
              onSave={newTime => {
                handleValueChange('update', newTime)
                handleModalClose()
              }}
            />
          )
        }
      })
    }
  }

  const handleRemoveTime = (id: string) => {
    const time = times.find(time => time.id === id)
    if (time) {
      return handleValueChange('delete', time)
    }
  }

  return (
    <Flex className={classes.timesList} column>
      <List isDisabled={isDisabled} label={label}>
        {times.map(time => (
          <List.Item
            key={time.id}
            isEditable
            isRemovable
            onDoubleClick={() => handleTimeEdit(time.id)}
            onEdit={() => handleTimeEdit(time.id)}
            onRemove={() => handleRemoveTime(time.id)}
          >
            <span style={{ padding: '0.5rem 0' }}>{time.time}</span>
          </List.Item>
        ))}
      </List>
      {!isDisabled && (
        <Flex style={{ marginTop: '1rem' }}>
          <Button iconPosition="left" id="add-time" xIcon="add" onClick={() => handleTimeAdd()}>
            {msg('common.controllers.timesList.addTime')}
          </Button>
        </Flex>
      )}
    </Flex>
  )
}

type TimesListControllerProps = {
  name: string,
  id?: string,
  label?: string,
  isDisabled?: boolean
}

const TimesListController = ({ id, name, label, isDisabled }: TimesListControllerProps) => {
  const { errors } = useFormContext()
  return (
    <Flex column>
      <Controller
        id={id || `times-list-controller-${name}`}
        name={name}
        render={({ field: { value, onChange } }) => (
          <TimesList isDisabled={isDisabled} label={label} value={value} onChange={onChange} />
        )}
      />
      <ControllerError errors={errors} name={name} />
    </Flex>
  )
}

export { TimesListController }
